import React, { useState, useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useLocation } from "wouter";
import { AuthButton } from "@/components/auth-button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Mail, CheckCircle } from 'lucide-react';
import { LegalAcceptanceModal } from "@/components/legal-acceptance-modal";
import { countries } from "@/lib/countries";

const signupReasons = [
  "I am a Bitcoin miner",
  "I hold Bitcoin",
  "I am researching wallets",
  "Other"
] as const;

const registerSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string()
    .min(12, "Password must be at least 12 characters")
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?].*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/, 
      "Password must contain at least 1 uppercase letter, 1 lowercase letter, 2 numbers, and 2 special characters"),
  confirmPassword: z.string().min(12, "Password must be at least 12 characters"),
  country: z.string().min(1, "Country is required"),
  signupReason: z.enum(signupReasons),
  otherReason: z.string().optional(),
  // Acceptance is enforced by the legal modal flow, not a form field.
  termsAccepted: z.boolean(),
  termsVersion: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine(
  (data) => !(data.signupReason === "Other" && (!data.otherReason || data.otherReason.trim() === "")), {
    message: "Please specify your reason",
    path: ["otherReason"],
  }
);

type RegisterFormValues = z.infer<typeof registerSchema>;

export default function SignupPage() {
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [showLegalModal, setShowLegalModal] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const { user, registerMutation } = useAuth();

  const registerForm = useForm<RegisterFormValues>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      country: "",
      signupReason: "I hold Bitcoin",
      otherReason: "",
      termsAccepted: false,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (user && user.emailVerified) {
      navigate("/");
    }
  }, [user, navigate]);

  const watchSignupReason = registerForm.watch("signupReason");
  const showOtherReasonField = watchSignupReason === "Other";

  const onRegisterSubmit = (data: RegisterFormValues) => {
    const { confirmPassword, ...userData } = data;
    const registrationData = {
      ...userData,
      username: userData.email, // Set username to email
      termsVersion: "1.0",
      otherReason: userData.signupReason === "Other" ? userData.otherReason : undefined,
    };
    registerMutation.mutate(registrationData, {
      onSuccess: (response: any) => {
        if (response.requiresVerification) {
          setUserEmail(response.email);
          setShowVerificationMessage(true);
        } else {
          navigate("/");
        }
      }
    });
  };

  // Runs only once all fields are valid (via handleSubmit, so isSubmitted flips
  // true and errors re-validate onChange — clearing as fields become valid).
  const handleValidSubmit = (data: RegisterFormValues) => {
    // If already accepted (e.g. retry after a server error), skip re-reading the docs.
    if (data.termsAccepted) {
      onRegisterSubmit(data);
    } else {
      setShowLegalModal(true);
    }
  };

  // Fired after the user has scrolled through and agreed to all three documents.
  const handleLegalComplete = () => {
    registerForm.setValue("termsAccepted", true, { shouldValidate: false });
    setShowLegalModal(false);
    registerForm.handleSubmit(onRegisterSubmit)();
  };

  // Show verification message after successful registration
  if (showVerificationMessage) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <div className="mb-6">
            <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Registration Almost Complete!
            </h1>
            <p className="text-gray-600 dark:text-gray-300">
              We've sent a verification link to
            </p>
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mt-2">
              <p className="text-blue-600 dark:text-blue-400 font-semibold break-all text-sm">
                {userEmail}
              </p>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <p className="text-green-800 dark:text-green-200 text-sm font-medium">
                📧 Next Steps:
              </p>
              <ol className="text-green-700 dark:text-green-300 text-sm mt-2 space-y-1 list-decimal list-inside">
                <li>Check your email inbox (and spam folder)</li>
                <li>Click the verification link in the email</li>
                <li>You'll be automatically logged in</li>
              </ol>
            </div>
            
            <p className="text-gray-600 dark:text-gray-300 text-sm">
              <strong>Didn't receive the email?</strong> Check your spam folder or try registering again.
            </p>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <p className="text-blue-800 dark:text-blue-200 text-sm">
                <strong>Note:</strong> The verification link will expire in 24 hours.
              </p>
            </div>
            
            <div className="space-y-2">
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Go to Login
              </button>
              <button
                onClick={() => {
                  setShowVerificationMessage(false);
                  registerForm.reset();
                }}
                className="w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Register Another Account
              </button>
            </div>
            
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Still having trouble? Contact support for assistance.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-100 dark:bg-gray-900">
      {/* Left column – Signup form */}
      <main className="flex flex-col items-center justify-center w-full lg:w-1/2 p-4 h-screen">
        <div className="flex flex-col items-center w-full max-w-md">
          <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden dark:border dark:border-gray-700">
            <div className="p-8">
              <div className="flex justify-center mb-6">
                <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-3">
                  <Mail className="h-6 w-6 text-gray-500 dark:text-gray-300" />
                </div>
              </div>
              <h2 className="text-2xl font-semibold tracking-wide text-center text-gray-800 dark:text-white mb-2">
                Let's get started
              </h2>
              <p className="text-center text-gray-600 dark:text-gray-300 mb-8">
                Enter your email address and password.
              </p>
              <Form {...registerForm}>
                <form onSubmit={registerForm.handleSubmit(handleValidSubmit)} className="space-y-6">
                  <div className="space-y-1">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                      Email
                    </label>
                    <FormField
                      control={registerForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input 
                              placeholder="your@email.com" 
                              className="w-full px-3 py-2 border border-gray-300 rounded-md" 
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage className="text-red-500 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="create-password" className="block text-sm font-medium text-gray-700">
                      Create password
                    </label>
                    <FormField
                      control={registerForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <Input 
                                type={showPassword ? "text" : "password"} 
                                placeholder="Minimum 12 characters, mixed case, 2 numbers, 2 special chars" 
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10" 
                                {...field} 
                              />
                              <button 
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowPassword(!showPassword)}
                              >
                                {showPassword ? (
                                  <EyeOff className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <Eye className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage className="text-red-500 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                      Confirm password
                    </label>
                    <FormField
                      control={registerForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <div className="relative">
                              <Input 
                                type={showConfirmPassword ? "text" : "password"} 
                                placeholder="Re-enter your password" 
                                className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10" 
                                {...field} 
                              />
                              <button 
                                type="button"
                                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                              >
                                {showConfirmPassword ? (
                                  <EyeOff className="h-5 w-5 text-gray-400" />
                                ) : (
                                  <Eye className="h-5 w-5 text-gray-400" />
                                )}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage className="text-red-500 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <FormField
                      control={registerForm.control}
                      name="country"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-gray-700">Country</FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-none">
                                <SelectValue placeholder="Select your country" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent className="max-h-[300px] overflow-y-auto">
                              {countries.map((country) => (
                                <SelectItem key={country} value={country} className="cursor-pointer">
                                  {country}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  <div className="space-y-1">
                    <FormField
                      control={registerForm.control}
                      name="signupReason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="block text-sm font-medium text-gray-700">
                            Why are you signing up?
                          </FormLabel>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 shadow-none">
                                <SelectValue placeholder="Select your reason" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {signupReasons.map((reason) => (
                                <SelectItem key={reason} value={reason} className="cursor-pointer">
                                  {reason}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage className="text-red-500 text-xs" />
                        </FormItem>
                      )}
                    />
                  </div>
                  {showOtherReasonField && (
                    <div className="space-y-1">
                      <FormField
                        control={registerForm.control}
                        name="otherReason"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="block text-sm font-medium text-gray-700">
                              Please specify
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Tell us why you're signing up"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage className="text-red-500 text-xs" />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                  <Button
                    type="submit"
                    disabled={registerMutation.isPending}
                    variant="default"
                    className="w-full"
                  >
                    {registerMutation.isPending ? "Creating account..." : "Sign Up"}
                  </Button>
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                    By clicking Sign Up, you'll review and agree to our Privacy Policy,
                    Terms of Service, and Disclaimer of DMG Blockchain Solutions.
                  </p>
                </form>
              </Form>
            </div>
          </div>
          <div className="mt-4 text-center w-full">
            <a href="/login" className="text-sm font-medium text-blue-600 hover:underline dark:text-blue-400">
              Already have an account? Log in
            </a>
          </div>
        </div>
        <footer className="py-4 text-center text-gray-500 text-sm lg:w-full">
          © blockseer.com. All rights reserved.
        </footer>
      </main>

      {/* Right column – Splash image */}
      <div className="w-full lg:w-1/2 relative hidden lg:block">
        <img src="/assets/login_spash.png" alt="Signup visual" className="w-full h-full object-cover" />
        <div className="w-full h-full bg-black/50 absolute top-0 left-0" />
        <div className="absolute bottom-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white text-3xl font-bold">
          <p className="font-bold">Connecting the blocks.</p>
          <p className="font-normal">Search. Discover. Analyze.</p>
        </div>
        <img src="/assets/Blockseer-logo-white.png" alt="Blockseer Logo" className="absolute top-4 right-4 w-56" />
      </div>

      <LegalAcceptanceModal
        open={showLegalModal}
        onOpenChange={setShowLegalModal}
        onComplete={handleLegalComplete}
        isSubmitting={registerMutation.isPending}
      />
    </div>
  );
} 
